'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './cn';
import { CONTROL_BASE, CONTROL_SIZE, CONTROL_TEXT, Field, type ControlSize } from './Field';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Options with the same `group` are wrapped in one `<optgroup>`. */
  group?: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: ControlSize;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Declarative options; `children` still works for hand-written markup. */
  options?: SelectOption[];
  /** Shown as a disabled first option when the value is empty. */
  placeholder?: string;
  containerClassName?: string;
}

/**
 * Deliberately a **native** `<select>`: it gets the OS picker on phones (a wheel on iOS),
 * costs nothing to open, and is announced correctly by every screen reader.
 * Use DropdownMenu when the items need icons, sections or non-value actions.
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    size = 'md',
    label,
    hint,
    error,
    options,
    placeholder,
    className,
    containerClassName,
    children,
    id: idProp,
    required,
    ...props
  },
  ref,
) {
  const grouped = React.useMemo(() => {
    if (!options) return null;
    const out: { group: string | undefined; items: SelectOption[] }[] = [];
    for (const opt of options) {
      const last = out[out.length - 1];
      if (last && last.group === opt.group) last.items.push(opt);
      else out.push({ group: opt.group, items: [opt] });
    }
    return out;
  }, [options]);

  const render = (ids: { id: string; describedBy?: string; invalid: boolean }) => (
    <div className="relative flex items-center">
      <select
        ref={ref}
        id={idProp ?? ids.id}
        required={required}
        aria-invalid={ids.invalid || undefined}
        aria-describedby={ids.describedBy}
        className={cn(
          CONTROL_BASE,
          CONTROL_TEXT,
          CONTROL_SIZE[size],
          'cursor-pointer appearance-none pe-9 bg-none',
          className,
        )}
        {...props}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {grouped
          ? grouped.map((g, i) =>
              g.group ? (
                <optgroup key={g.group} label={g.group}>
                  {g.items.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              ) : (
                <React.Fragment key={`g${i}`}>
                  {g.items.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </option>
                  ))}
                </React.Fragment>
              ),
            )
          : children}
      </select>
      <ChevronDown className="pointer-events-none absolute end-3 size-4 text-ink-3" aria-hidden />
    </div>
  );

  if (!label && !hint && !error) {
    return <>{render({ id: idProp ?? '', describedBy: props['aria-describedby'], invalid: false })}</>;
  }
  return (
    <Field label={label} hint={hint} error={error} required={required} className={containerClassName}>
      {(ids) => render(ids)}
    </Field>
  );
});
